import axios from "axios";
import {
  getAccessToken,
  getRefreshToken,
  setTokens,
  removeTokens,
} from "./storage";

const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

axiosInstance.interceptors.request.use(
  function (config) {
    // Add the authorization header with the access token
    const accessToken = getAccessToken();
    if (accessToken) {
      config.headers["Authorization"] = `Bearer ${accessToken}`;
    }
    return config;
  },
  function (error) {
    // Do something with request error
    return Promise.reject(error);
  }
);

// Sends the user back to login when the session can't be recovered.
const forceLogout = () => {
  removeTokens();
  // Full navigation rather than a router push: this runs outside React, and
  // reloading re-initialises the store from the now-empty storage. Only the
  // query string is carried over -- /login is an exact route, so appending the
  // old pathname would land on NotFound. The guard keeps a failing request on
  // the login page from looping.
  const { pathname, search } = window.location;
  if (pathname !== "/login") {
    window.location.replace(`/login${search}`);
  }
};

// De-duplicates concurrent refreshes: when several requests 401 at once they
// all await the same in-flight refresh instead of each spending the (rotated)
// refresh token.
let refreshPromise = null;

// Exchanges the stored refresh token for a fresh access+refresh pair. Uses a
// BARE axios (no interceptors) so a 401 here can't recurse back into this
// handler. Participants must use /meeting-hub/auth/refresh — the admin
// /auth/refresh rejects MEETING_HUB tokens. Returns the new access token, or
// null on failure.
const performTokenRefresh = async () => {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return null;
  try {
    const res = await axios.post(
      `${process.env.REACT_APP_BACKEND_URL}/meeting-hub/auth/refresh`,
      { token: refreshToken },
      { headers: { "Content-Type": "application/json" } }
    );
    const accessToken = res.data?.access?.token;
    const newRefreshToken = res.data?.refresh?.token;
    if (!accessToken || !newRefreshToken) return null;
    setTokens({ accessToken, refreshToken: newRefreshToken });
    return accessToken;
  } catch (e) {
    return null;
  }
};

axiosInstance.interceptors.response.use(
  function (response) {
    return response;
  },
  async function (error) {
    const original = error.config;
    const status = error.response?.status;
    // Never try to refresh on the auth endpoints themselves (login, verify,
    // resend, refresh) — a 401 there is a real credential/session failure.
    const isAuthCall = original?.url?.includes("/auth/");

    if (status === 401 && original && !original._retry && !isAuthCall) {
      original._retry = true;

      if (!refreshPromise) {
        refreshPromise = performTokenRefresh().finally(() => {
          refreshPromise = null;
        });
      }
      const newAccessToken = await refreshPromise;

      if (newAccessToken) {
        // Retry the original request once with the fresh token.
        original.headers = original.headers || {};
        original.headers["Authorization"] = `Bearer ${newAccessToken}`;
        return axiosInstance(original);
      }

      // Refresh failed (missing/expired/rejected) — kick the user out.
      forceLogout();
    }

    return Promise.reject(error);
  }
);

export { axiosInstance };
